import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Newspaper, ArrowRight, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

function ArticleGrid({ items, onOpen, emptyTitle, emptyHint, testid }) {
  if (items.length === 0) {
    return (
      <Card className="p-12 text-center border border-slate-200" data-testid={`${testid}-empty`}>
        <Newspaper className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-700 font-medium">{emptyTitle}</p>
        <p className="text-sm text-slate-400 mt-1">{emptyHint}</p>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`${testid}-grid`}>
      {items.map((a) => (
        <button key={a.id} onClick={() => onOpen(a)} data-testid={`${testid}-card-${a.id}`}
          className="group text-left bg-white rounded-xl border border-slate-200 hover:border-brand/40 hover:shadow-md transition-all overflow-hidden flex flex-col">
          {a.featured_image && (
            <div className="h-40 w-full bg-slate-100 overflow-hidden">
              <img src={a.featured_image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-5 flex flex-col flex-1">
            <p className="text-xs text-slate-400 mb-1">{fmtDate(a.published_at)}</p>
            <h3 className="text-lg font-semibold text-slate-900 group-hover:text-brand transition-colors leading-snug">{a.title}</h3>
            {a.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{a.excerpt}</p>}
            <span className="inline-flex items-center gap-1 text-sm font-medium text-brand mt-3">
              Read more <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}

export default function EmployerNews() {
  const [user, setUser] = useState(null);
  const [local, setLocal] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'updates' ? 'updates' : 'local';

  useEffect(() => {
    (async () => {
      try {
        const [u, n, g] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/regional/news`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/platform/content?type=news`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setUser(u.data); setLocal(n.data || []); setUpdates(g.data || []);
      } catch (e) { /* empty states */ } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/employer/dashboard')} data-testid="news-back-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-10 rounded-lg bg-brand-light flex items-center justify-center flex-shrink-0">
            <Newspaper className="w-5 h-5 text-brand" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">News</h1>
            <p className="text-sm text-slate-500">Local workforce news and broader updates for employers.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setParams(v === 'updates' ? { tab: 'updates' } : {}, { replace: true })}>
            <TabsList className="bg-slate-100 rounded-lg p-1 mb-6">
              <TabsTrigger value="local" data-testid="news-tab-local" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Local News</TabsTrigger>
              <TabsTrigger value="updates" data-testid="news-tab-updates" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Workforce Updates</TabsTrigger>
            </TabsList>
            <TabsContent value="local">
              <ArticleGrid items={local} testid="news-local"
                onOpen={(a) => navigate(`/employer/news/${a.id}`)}
                emptyTitle="No local news has been published yet."
                emptyHint="Your regional team hasn't published any articles." />
            </TabsContent>
            <TabsContent value="updates">
              <ArticleGrid items={updates} testid="news-updates"
                onOpen={(a) => navigate(`/employer/article/news/${a.id}`)}
                emptyTitle="No workforce updates right now."
                emptyHint="Broader workforce updates will appear here when available." />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
}
